import type { Node, NodeDimensionChange } from '../../core';
import type { HandleBoundsUpdate, InternalNode, MindmapState } from '../types';

const toInternalNode = (node: Node, previous: InternalNode | undefined): InternalNode => ({
  node,
  positionAbsolute: node.position,
  width: node.width ?? previous?.width ?? null,
  height: node.height ?? previous?.height ?? null,
  handleBounds: previous?.handleBounds ?? null,
});

/** Lookup by id; entries whose node object is unchanged keep their identity so subscribers skip re-rendering. */
export const buildNodeLookup = (
  nodes: readonly Node[],
  previous: ReadonlyMap<string, InternalNode>,
): Map<string, InternalNode> => {
  const lookup = new Map<string, InternalNode>();
  nodes.forEach((node) => {
    const existing = previous.get(node.id);
    lookup.set(node.id, existing?.node === node ? existing : toInternalNode(node, existing));
  });
  return lookup;
};

/** State after the app passes a new `nodes` array (controlled mode) or the engine replaces it. */
export const withNodes = (
  previousLookup: ReadonlyMap<string, InternalNode>,
  nodes: Node[],
): Pick<MindmapState, 'nodes' | 'nodeLookup'> => ({
  nodes,
  nodeLookup: buildNodeLookup(nodes, previousLookup),
});

/** Only the changes that carry a size different from what the engine already knows (avoids measure loops). */
export const getEffectiveDimensionChanges = (
  lookup: ReadonlyMap<string, InternalNode>,
  changes: readonly NodeDimensionChange[],
): NodeDimensionChange[] =>
  changes.filter(({ id, dimensions }) => {
    const entry = lookup.get(id);
    if (!entry || !dimensions?.width || !dimensions.height) return false;
    return entry.width !== dimensions.width || entry.height !== dimensions.height;
  });

/**
 * Size changes for measured nodes whose node object lacks that size — e.g. the app swapped in fresh objects from the
 * API after showing a cached copy. The DOM size did not change, so no measurement fires; v11 still writes the size
 * back into such nodes, and the app relies on `node.width/height` (sidebar drop finds the nearest node with them).
 */
export const getUnreportedSizeChanges = (lookup: ReadonlyMap<string, InternalNode>): NodeDimensionChange[] => {
  const changes: NodeDimensionChange[] = [];
  lookup.forEach(({ node, width, height }) => {
    if (node.hidden || width === null || height === null) return;
    if (node.width !== width || node.height !== height) {
      changes.push({ id: node.id, type: 'dimensions', dimensions: { width, height } });
    }
  });
  return changes;
};

/** Lookup with the measured sizes recorded; only the changed entries get new identities. */
export const withMeasuredSizes = (
  lookup: ReadonlyMap<string, InternalNode>,
  changes: readonly NodeDimensionChange[],
): Pick<MindmapState, 'nodeLookup'> => {
  const next = new Map(lookup);
  changes.forEach(({ id, dimensions }) => {
    const entry = next.get(id);
    if (entry && dimensions) next.set(id, { ...entry, width: dimensions.width, height: dimensions.height });
  });
  return { nodeLookup: next };
};

/** Lookup with new handle bounds; only the updated entries get new identities. */
export const withHandleBounds = (
  lookup: ReadonlyMap<string, InternalNode>,
  updates: readonly HandleBoundsUpdate[],
): Pick<MindmapState, 'nodeLookup'> => {
  const next = new Map(lookup);
  updates.forEach(({ id, handleBounds }) => {
    const entry = next.get(id);
    if (entry) next.set(id, { ...entry, handleBounds });
  });
  return { nodeLookup: next };
};
